import app from 'flarum/forum/app';

app.initializers.add('ekumanov/flarum-ext-inline-audio', () => {
    const audioRe = /\.(mp3|wav|ogg|flac|m4a|mpeg|mpg|mp4|wave|aac|webm)(\?[^#]*)?(#.*)?$/i;

    // ── Build the global player bar ───────────────────────────────────────────

    const bar = document.createElement('div');
    bar.className = 'pc-player-bar';
    bar.hidden = true;
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Audio player');

    const barName = document.createElement('button');
    barName.className = 'pc-player-bar-name';
    barName.setAttribute('aria-label', 'Scroll to post');

    const barAudio = document.createElement('audio');
    barAudio.controls = true;
    barAudio.preload = 'none';

    const barDownload = document.createElement('a');
    barDownload.className = 'pc-player-bar-download';
    barDownload.setAttribute('aria-label', 'Download');

    const barClose = document.createElement('button');
    barClose.className = 'pc-player-bar-close';
    barClose.setAttribute('aria-label', 'Close player');
    barClose.textContent = '✕';

    bar.append(barName, barAudio, barDownload, barClose);
    document.body.appendChild(bar);

    // ── Track the active filename button ──────────────────────────────────────

    let currentBtn = null;

    function setCurrentBtn(btn) {
        if (currentBtn) {
            currentBtn.removeAttribute('data-current');
            currentBtn.removeAttribute('data-playing');
            currentBtn.setAttribute('aria-label', 'Play ' + currentBtn.textContent);
        }
        currentBtn = btn;
        if (btn) {
            btn.setAttribute('data-current', '');
            btn.setAttribute('aria-label', 'Pause ' + btn.textContent);
        }
    }

    // ── Load a track into the bar ─────────────────────────────────────────────

    let downloadVisibilitySet = false;

    function loadTrack(url, name, btn) {
        if (!downloadVisibilitySet) {
            barDownload.hidden = app.forum.attribute('ekumanov-inline-audio.showDownloadButton') === false;
            downloadVisibilitySet = true;
        }
        setCurrentBtn(btn);
        barName.textContent = name;
        barName.setAttribute('aria-label', 'Scroll to post: ' + name);
        barAudio.src = url;
        barDownload.href = url;
        barDownload.setAttribute('download', name);
        barDownload.setAttribute('aria-label', 'Download ' + name);
        bar.hidden = false;
        updateMediaSession(name);
        if (app.forum.attribute('ekumanov-inline-audio.autoPlay') !== false) barAudio.play();
    }

    // ── Media Session API (lock screen / OS media controls) ──────────────────

    function updateMediaSession(name) {
        if (!('mediaSession' in navigator)) return;
        try {
            navigator.mediaSession.metadata = new MediaMetadata({ title: name });
        } catch (e) { /* ignore */ }
    }

    function clearMediaSession() {
        if (!('mediaSession' in navigator)) return;
        try { navigator.mediaSession.metadata = null; } catch (e) { /* ignore */ }
    }

    if ('mediaSession' in navigator) {
        const safeSet = (action, handler) => {
            try { navigator.mediaSession.setActionHandler(action, handler); } catch (e) { /* unsupported action */ }
        };
        safeSet('play', () => barAudio.play());
        safeSet('pause', () => barAudio.pause());
        safeSet('stop', () => { barAudio.pause(); barAudio.currentTime = 0; });
    }

    // ── Download helper (used by bar download button) ─────────────────────────

    function triggerDownload(url, filename) {
        fetch(url)
            .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
            .then((blob) => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(a.href), 10000);
            })
            .catch(() => window.open(url, '_blank'));
    }

    // ── Bar controls ──────────────────────────────────────────────────────────

    barDownload.addEventListener('click', (e) => {
        e.preventDefault();
        triggerDownload(barDownload.href, barDownload.getAttribute('download') || barDownload.href.split('/').pop());
    });

    barName.addEventListener('click', () => {
        if (currentBtn) currentBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    barClose.addEventListener('click', () => {
        barAudio.pause();
        barAudio.src = '';
        bar.hidden = true;
        setCurrentBtn(null);
        clearMediaSession();
    });

    barAudio.addEventListener('play', () => {
        bar.setAttribute('data-playing', '');
        if (currentBtn) {
            currentBtn.setAttribute('data-playing', '');
            currentBtn.setAttribute('aria-label', 'Pause ' + currentBtn.textContent);
        }
    });

    barAudio.addEventListener('pause', () => {
        bar.removeAttribute('data-playing');
        if (currentBtn) {
            currentBtn.removeAttribute('data-playing');
            currentBtn.setAttribute('aria-label', 'Resume ' + currentBtn.textContent);
        }
    });

    barAudio.addEventListener('ended', () => {
        bar.hidden = true;
        setCurrentBtn(null);
    });

    // ── Adjust bar position when Flarum composer is open ─────────────────────

    function adjustBarForComposer() {
        const composer = document.querySelector('.Composer');
        if (!composer || composer.classList.contains('Composer--minimized')) {
            bar.style.bottom = '';
        } else {
            bar.style.bottom = composer.offsetHeight + 'px';
        }
    }

    new MutationObserver(adjustBarForComposer).observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
    });

    // ── Helpers ───────────────────────────────────────────────────────────────

    function getFilename(a) {
        let text = a.textContent.trim();
        const host = a.href.replace(/^https?:\/\//, '').split('/')[0];
        const di = text.indexOf(host);
        if (di > 0 && text.substring(di - 3, di) !== '://') text = text.substring(0, di).trim();
        const extMatch = text.match(/^(.+?\.(mp3|wav|ogg|flac|m4a|mpeg|mpg|mp4|wave|aac|webm))/i);
        if (extMatch) text = extMatch[1];
        if (/^https?:\/\//.test(text)) {
            try { return decodeURIComponent(text.split('/').pop().split('?')[0]); }
            catch (e) { return text.split('/').pop().split('?')[0]; }
        }
        return text || decodeURIComponent(a.href.split('/').pop().split('?')[0]);
    }

    function filenameFromUrl(url) {
        try { return decodeURIComponent(url.split('/').pop().split('?')[0]); }
        catch (e) { return url.split('/').pop().split('?')[0]; }
    }

    function stripUploadPrefix(name) {
        if (app.forum.attribute('ekumanov-inline-audio.stripUploadPrefix') === false) return name;
        return name.replace(/^\d+-\d+-/, '');
    }

    function makeButton(url, name) {
        name = stripUploadPrefix(name);
        const useLink = app.forum.attribute('ekumanov-inline-audio.showRightClickDownload') !== false;
        const btn = document.createElement(useLink ? 'a' : 'button');
        btn.className = 'pc-audio-name';
        btn.setAttribute('data-audio-url', url);
        btn.setAttribute('data-ap', '1');
        btn.setAttribute('aria-label', 'Play ' + name);
        btn.title = name;
        const nameSpan = document.createElement('span');
        nameSpan.className = 'pc-audio-name-text';
        nameSpan.textContent = name;
        btn.appendChild(nameSpan);
        if (useLink) {
            btn.href = url;
            btn.setAttribute('download', name);
        }
        btn.addEventListener('click', (e) => {
            if (useLink) e.preventDefault();
            if (btn === currentBtn) {
                barAudio.paused ? barAudio.play() : barAudio.pause();
            } else {
                loadTrack(url, name, btn);
            }
        });
        return btn;
    }

    // ── Process post ──────────────────────────────────────────────────────────

    function processPost(el) {
        // Auto-detected audio links → replace <a> with <span><button>
        el.querySelectorAll('a[href]:not([data-ap])').forEach((a) => {
            if (!audioRe.test(a.getAttribute('href'))) return;
            a.setAttribute('data-ap', '1');

            const wrap = document.createElement('span');
            wrap.className = 'pc-audio-wrap';
            wrap.appendChild(makeButton(a.href, getFilename(a)));
            a.parentNode.replaceChild(wrap, a);
        });

        // [player] BBCode → PHP outputs <span class="pc-audio-wrap" data-audio-url="...">
        el.querySelectorAll('span.pc-audio-wrap[data-audio-url]:not([data-ap])').forEach((wrap) => {
            wrap.setAttribute('data-ap', '1');
            const url = wrap.getAttribute('data-audio-url');
            wrap.appendChild(makeButton(url, filenameFromUrl(url)));
        });
    }

    // ── MutationObserver ──────────────────────────────────────────────────────

    new MutationObserver((muts) => {
        muts.forEach((m) => {
            m.addedNodes.forEach((node) => {
                if (node.nodeType !== 1) return;
                if (node.classList && node.classList.contains('Post-body')) {
                    processPost(node);
                } else if (node.querySelectorAll) {
                    const bodies = node.querySelectorAll('.Post-body');
                    bodies.forEach((b) => processPost(b));
                    if (!bodies.length && node.closest) {
                        const parent = node.closest('.Post-body');
                        if (parent) processPost(parent);
                    }
                }
            });
        });
    }).observe(document.documentElement, { childList: true, subtree: true });
});
